/**
 * Capa 5: Vista Principal del Asistente de Registro — OnboardingWizardView
 * Adaptación Fiel Desktop (1368x720px) & Móvil
 * Paso 1: Predio Avícola (#c25ee8)
 * Paso 2: Cuenta y Titular Legal (#70a32c Verde Orgánico)
 */

'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { ROUTES } from '@/config/routes';
import { useOnboardingWizard } from '../hooks/use-onboarding-wizard';
import { Step1GranjaForm } from '../components/Step1GranjaForm';
import { Step2UsuarioForm } from '../components/Step2UsuarioForm';
import { Step3CredencialesForm } from '../components/Step3CredencialesForm';
import { Step4BienvenidaView } from '../components/Step4BienvenidaView';
import { IdiomaModalSelector, IdiomaItem } from '../components/IdiomaModalSelector';
import { OnboardingErrorModal } from '../components/OnboardingErrorModal';
import {
  SocialIndividualModal,
  type RedSocialId,
} from '@/shared/components/modals';
import styles from './OnboardingWizardView.module.css';

const IDIOMAS: IdiomaItem[] = [
  {
    code: 'es',
    label: 'Español',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#c60b1e" />
        <path d="M0 120h640v240H0z" fill="#ffc400" />
      </svg>
    ),
  },
  {
    code: 'en',
    label: 'English',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#012169" />
        <path
          d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-179L0 64V0h75z"
          fill="#FFF"
        />
        <path
          d="M424 281l216 160v39l-270-200h54zm180-281l-228 169h54L640 40V0h-36zM0 39l230 170h-54L0 70v-31zm0 402l222-164h55L0 460v-19z"
          fill="#C8102E"
        />
        <path d="M240 0h160v480H240zM0 160h640v160H0z" fill="#FFF" />
        <path d="M272 0h96v480h-96zM0 192h640v96H0z" fill="#C8102E" />
      </svg>
    ),
  },
  {
    code: 'pt',
    label: 'Português',
    flag: (
      <svg className="w-full h-full object-cover" viewBox="0 0 640 480">
        <path d="M0 0h640v480H0z" fill="#009b3a" />
        <path d="M320 40L600 240 320 440 40 240z" fill="#fedf00" />
        <circle cx="320" cy="240" fill="#002776" r="105" />
        <path d="M218 250a110 110 0 0 0 204-18 105 105 0 0 1-204 18z" fill="#FFF" />
      </svg>
    ),
  },
];

/* ─── Variantes Cinemáticas Framer Motion para Transición entre Pasos ─── */
const stepTransitionVariants: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? 30 : -30,
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1],
    },
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.35,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction > 0 ? -30 : 30,
    transition: {
      duration: 0.22,
      ease: 'easeInOut',
    },
  }),
};

const cardMotionVariants: Variants = {
  enter: (direction: number) => ({
    opacity: 0,
    scale: 0.985,
    x: direction > 0 ? 25 : -25,
  }),
  center: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1],
    },
  },
  exit: (direction: number) => ({
    opacity: 0,
    scale: 0.985,
    x: direction > 0 ? -25 : 25,
    transition: {
      duration: 0.22,
      ease: 'easeInOut',
    },
  }),
};

const elieVariants: Variants = {
  enter: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
  },
  center: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: { duration: 0.25, ease: 'easeInOut' },
  },
};

const bgSpringTransition = {
  duration: 0.5,
  ease: [0.16, 1, 0.3, 1] as const,
};

export function OnboardingWizardView() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const resumeTokenHandledRef = useRef(false);

  const [isIdiomaModalOpen, setIsIdiomaModalOpen] = useState(false);
  const [showElieTooltip, setShowElieTooltip] = useState(false);

  // Estados para Modales Globales Reutilizables
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const [activeSocialNetwork, setActiveSocialNetwork] = useState<RedSocialId>('INSTAGRAM');

  const handleOpenSocial = (red: RedSocialId) => {
    setActiveSocialNetwork(red);
    setIsSocialModalOpen(true);
  };

  const handleOpenWhatsApp = () => {
    setActiveSocialNetwork('WHATSAPP');
    setIsSocialModalOpen(true);
  };

  const {
    pasoActual,
    direccionAnimacion,
    paso1,
    paso2,
    paso3,
    emailValidation,
    isSubmitting,
    updatePaso1,
    guardarPaso1,
    guardarPaso2,
    completarRegistro,
    finalizarOnboarding,
    validarEmailAntiColision,
    retrocederPaso,
    setPasoActual,
    abandonarOnboarding,
    recuperarDesdeMagicLink,
    errorModal,
    cerrarErrorModal,
    mostrarErrorModal,
    resetValidaciones,
  } = useOnboardingWizard();

  const isStep2 = pasoActual === 2;
  const isStep3 = pasoActual === 3;
  const isStep4 = pasoActual === 4;
  const currentThemeColor = isStep4 ? '#c25ee8' : isStep3 ? '#EE5847' : isStep2 ? '#70a32c' : '#c25ee8';

  // Soporte para parámetro URL ?step=1, ?step=2, ?step=3, ?step=4 para pruebas directas o enlaces externos
  useEffect(() => {
    const stepParam = searchParams?.get('step');
    if (stepParam === '4' && pasoActual !== 4) {
      setPasoActual(4, 1);
    } else if (stepParam === '3' && pasoActual !== 3) {
      setPasoActual(3, stepParam > '3' ? -1 : 1);
    } else if (stepParam === '2' && pasoActual !== 2) {
      setPasoActual(2, stepParam > '2' ? -1 : 1);
    } else if (stepParam === '1' && pasoActual !== 1) {
      setPasoActual(1, -1);
    }
  }, [searchParams, pasoActual, setPasoActual]);

  // Detectar ?resume_token= del Magic Link de WhatsApp
  useEffect(() => {
    const resumeToken = searchParams?.get('resume_token');
    if (resumeToken && !resumeTokenHandledRef.current) {
      resumeTokenHandledRef.current = true;
      recuperarDesdeMagicLink(resumeToken);
    }
  }, [searchParams, recuperarDesdeMagicLink]);

  // Limpiar parámetros de depuración de la URL si quedaron en el navegador
  useEffect(() => {
    if (typeof window !== 'undefined' && (window.location.search.includes('error=') || window.location.search.includes('error_modal='))) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Manejo de scroll en desktop vs móvil
  useEffect(() => {
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverscroll = document.body.style.overscrollBehavior;
    const prevHtmlOverscroll = document.documentElement.style.overscrollBehavior;

    const applyScrollBehavior = () => {
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overscrollBehavior = 'none';
        document.documentElement.style.overscrollBehavior = 'none';
      } else {
        document.body.style.overflowX = 'hidden';
        document.documentElement.style.overflowX = 'hidden';
        document.body.style.overflowY = 'auto';
        document.documentElement.style.overflowY = 'auto';
        document.body.style.overscrollBehavior = 'auto';
        document.documentElement.style.overscrollBehavior = 'auto';
        document.body.style.touchAction = 'pan-y';
      }
    };

    applyScrollBehavior();
    window.addEventListener('resize', applyScrollBehavior);

    const handlePreventScroll = (e: WheelEvent) => {
      if (window.innerWidth <= 768) {
        return; // Permitir scroll natural en móviles y tablets
      }
      const target = e.target as HTMLElement | null;
      if (target) {
        let el: HTMLElement | null = target;
        while (el && el !== document.body && el !== document.documentElement) {
          if (
            el.getAttribute('data-scrollable') === 'true' ||
            (el.className && typeof el.className === 'string' && el.className.toLowerCase().includes('scroll'))
          ) {
            return;
          }
          const style = window.getComputedStyle(el);
          if (
            (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
            el.scrollHeight > el.clientHeight
          ) {
            return;
          }
          el = el.parentElement;
        }
      }
      e.preventDefault();
    };

    window.addEventListener('wheel', handlePreventScroll, { passive: false });

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overscrollBehavior = prevBodyOverscroll;
      document.documentElement.style.overscrollBehavior = prevHtmlOverscroll;
      window.removeEventListener('resize', applyScrollBehavior);
      window.removeEventListener('wheel', handlePreventScroll);
    };
  }, []);

  const currentLang = IDIOMAS.find((i) => i.code === (paso1.idioma || 'es')) || IDIOMAS[0];

  const handleSelectLang = (item: IdiomaItem) => {
    updatePaso1({ idioma: item.code });
    setIsIdiomaModalOpen(false);
  };

  const handleVolverPaso1 = () => {
    if (window.history.length > 1) {
      router.back();
    } else {
      abandonarOnboarding();
    }
  };

  return (
    <motion.div
      className={`${styles.pageWrapper} ${
        isStep3 ? styles.pageWrapperStep3 : isStep2 ? styles.pageWrapperStep2 : ''
      }`}
      animate={{
        backgroundColor: currentThemeColor,
      }}
      transition={bgSpringTransition}
    >
      {/* ─── Lienzo Principal (1368x720 en Desktop | max-w-md en Móvil) ─── */}
      <motion.div
        className={`${styles.mainCanvas} ${
          isStep3 ? styles.mainCanvasStep3 : isStep2 ? styles.mainCanvasStep2 : ''
        }`}
        animate={{
          backgroundColor: currentThemeColor,
        }}
        transition={bgSpringTransition}
      >
        {/* ─── TopBar Exclusivo de Móvil (Compartido entre Paso 1, 2 y 3) ─── */}
        {!isStep4 && (
          <motion.header
            className={styles.mobileHeader}
            data-purpose="mobile-header"
            animate={{
              backgroundColor: currentThemeColor,
            }}
            transition={bgSpringTransition}
          >
            {/* Botón Volver Móvil */}
            <button
              type="button"
              onClick={pasoActual > 1 ? retrocederPaso : handleVolverPaso1}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/25 hover:bg-white/35 active:scale-95 backdrop-blur-md text-white transition-all cursor-pointer flex items-center justify-center border border-white/20 shadow-xs"
              aria-label="Volver"
              title="Volver"
            >
              <span className="material-symbols-outlined text-[19px] sm:text-[20px]">arrow_back</span>
            </button>

            {/* Logo AviFácil Centrado Móvil */}
            <Link
              href={ROUTES.HOME}
              className="flex items-center gap-1.5"
              data-purpose="brand-logo"
              title="AviFácil"
            >
              <div className="w-8 h-8 rounded-full bg-[#FFB800] p-1 flex items-center justify-center shadow-md overflow-hidden flex-shrink-0">
                <Image
                  src="/Sign-up/avifacil-logo-badge.png"
                  alt="AviFácil Iconotipo"
                  width={24}
                  height={24}
                  className="w-full h-full object-contain pointer-events-none"
                  priority
                />
              </div>
              <span
                className="text-2xl text-white tracking-wide drop-shadow-sm font-bold leading-none"
                style={{ fontFamily: "'Caveat', cursive" }}
              >
                AviFácil
              </span>
            </Link>

            {/* Selector de Idioma Móvil (Solo Bandera en Móvil) */}
            <button
              type="button"
              onClick={() => setIsIdiomaModalOpen(true)}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/25 hover:bg-white/35 active:scale-95 backdrop-blur-md text-white transition-all cursor-pointer flex items-center justify-center border border-white/20 shadow-xs p-1"
              aria-expanded={isIdiomaModalOpen}
              aria-haspopup="dialog"
              aria-label={`Idioma actual: ${currentLang.label}. Toca para cambiar.`}
              title={currentLang.label}
            >
              <span className="w-5 h-3.5 rounded-[2.5px] overflow-hidden inline-flex flex-shrink-0 shadow-2xs border border-white/40">
                {currentLang.flag}
              </span>
            </button>
          </motion.header>
        )}

        {/* ══════════════════════════════════════════════════════════════════════════
           ─── TRANSICIÓN CINEMATOGRÁFICA FRAMER MOTION ENTRE PASO 1, 2 Y 3 ───
           ══════════════════════════════════════════════════════════════════════════ */}
        <AnimatePresence mode="wait" custom={direccionAnimacion}>
          {pasoActual === 1 ? (
            /* ────────────────────────────────────────────────────────────────────────
               ─── PASO 1: Predio Avícola (#c25ee8) ───
               ──────────────────────────────────────────────────────────────────────── */
            <motion.div
              key="step-1"
              custom={direccionAnimacion}
              variants={stepTransitionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full relative flex flex-col md:flex-row md:h-full md:overflow-hidden h-auto min-h-full overflow-visible flex-1"
            >
              {/* Columna Izquierda Desktop (45%): Logo, Redes Sociales */}
              <div className={styles.leftColumn}>
                <div className={styles.logoContainer}>
                  <Link
                    href={ROUTES.HOME}
                    className="flex items-center gap-2.5 group"
                    title="AviFácil — Inicio"
                  >
                    <div
                      className="rounded-full bg-[#FFB800] flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105 flex-shrink-0"
                      style={{ width: '36px', height: '36px' }}
                    >
                      <Image
                        src="/Sign-up/avifacil-logo-badge.png"
                        alt="Logo AviFácil"
                        width={22}
                        height={22}
                        priority
                        className="object-contain drop-shadow-xs w-5 h-5 pointer-events-none"
                      />
                    </div>
                    <span
                      className="text-white font-bold text-2xl lg:text-[26px] tracking-wide select-none drop-shadow-xs leading-none"
                      style={{ fontFamily: "'Caveat', cursive" }}
                    >
                      AviFácil
                    </span>
                  </Link>
                </div>

                {/* Iconos de Redes Sociales Verticales Desktop */}
                <div className={styles.socialContainer}>
                  <button
                    type="button"
                    aria-label="Instagram"
                    title="Instagram AviFácil"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
                    onClick={() => handleOpenSocial('INSTAGRAM')}
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Facebook"
                    title="Facebook AviFácil"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
                    onClick={() => handleOpenSocial('FACEBOOK')}
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="YouTube"
                    title="YouTube AviFácil"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
                    onClick={() => handleOpenSocial('YOUTUBE')}
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="Google"
                    title="Google AviFácil"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
                    onClick={() => handleOpenSocial('GOOGLE')}
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label="TikTok"
                    title="TikTok AviFácil"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#c25ee8] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer border-0"
                    onClick={() => handleOpenSocial('TIKTOK')}
                  >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tarjeta Derecha Blanca (Desktop 65% | Bottom Sheet en Móvil) */}
              <motion.div
                variants={cardMotionVariants}
                custom={direccionAnimacion}
                className={styles.rightCard}
              >
                {/* Botones Superiores Desktop (Ocultos en Móvil) */}
                <div className={styles.desktopTopActions}>
                  {/* Botón Volver Desktop */}
                  <div className="absolute top-8 left-8 z-20">
                    <button
                      type="button"
                      onClick={handleVolverPaso1}
                      className={styles.topActionBtn}
                    >
                      <span className="material-symbols-outlined text-[16px] text-gray-500">arrow_back</span>
                      <span>Volver</span>
                    </button>
                  </div>

                  {/* Selector de Idioma Desktop */}
                  <div className="absolute top-8 right-8 z-20">
                    <button
                      type="button"
                      id="lang-button"
                      aria-expanded={isIdiomaModalOpen}
                      aria-haspopup="dialog"
                      onClick={() => setIsIdiomaModalOpen(true)}
                      className={styles.topActionBtn}
                    >
                      <span className="w-4.5 h-3 rounded-xs overflow-hidden inline-flex shadow-2xs border border-gray-100 flex-shrink-0">
                        {currentLang.flag}
                      </span>
                      <span className="text-[11.5px] font-semibold tracking-wide text-gray-800">
                        {currentLang.label}
                      </span>
                      <span className="material-symbols-outlined text-[16px] text-gray-400">
                        expand_more
                      </span>
                    </button>
                  </div>
                </div>

                {/* Formulario Paso 1 Centrado */}
                <Step1GranjaForm
                  initialData={paso1}
                  onSubmit={guardarPaso1}
                  onCancel={abandonarOnboarding}
                />

                {/* Footer Móvil */}
                <footer className={styles.mobileSheetFooter} data-purpose="page-footer">
                  <p className="text-[11px] text-gray-400">
                    © 2026 AviFácil. Todos los derechos reservados.
                  </p>
                </footer>

                {/* Espacio en blanco móvil para que los botones CTA suban holgadamente sobre la barra de navegación */}
                <div className={styles.mobileScrollSpacer} aria-hidden="true" />
              </motion.div>

              {/* Botones Flotantes Desktop Paso 1 (WhatsApp y Avatar Elie) */}
              <div className={styles.floatingElieAvatarDesktop}>
                {showElieTooltip && (
                  <div className="absolute bottom-16 right-0 w-64 bg-white rounded-2xl p-3 shadow-xl border border-purple-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-40">
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <span className="font-bold text-[#c25ee8]">¡Hola! Soy Elie 🐥</span>
                      <button
                        type="button"
                        onClick={() => setShowElieTooltip(false)}
                        className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-600 leading-relaxed">
                      Estoy aquí para acompañarte en tu registro. Si tienes cualquier duda sobre tus datos, ¡avísame!
                    </p>
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowElieTooltip((prev) => !prev)}
                    aria-label="Abrir ayuda de Elie Asistente"
                    className="w-14 h-14 rounded-full bg-[#c25ee8] border-2 border-white shadow-xl overflow-hidden flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105 focus:outline-none"
                  >
                    <Image
                      src="/Sign-up/elie-avatar.webp"
                      alt="Avatar de Elie Asistente"
                      width={56}
                      height={56}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center top' }}
                    />
                  </button>
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-xs pointer-events-none z-10" />
                </div>
              </div>

              <div className={styles.floatingWhatsappDesktop}>
                <span
                  className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#c25ee8] ${styles.waRadarRing}`}
                />
                <span
                  className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#c25ee8] ${styles.waRadarRingDelayed}`}
                />
                <a
                  aria-label="Contactar por WhatsApp"
                  className={`relative flex items-center justify-center w-14 h-14 rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer group bg-[#c25ee8] hover:bg-[#b048d6] ${styles.waGlowBtn}`}
                  href="https://wa.me/573000000000?text=Hola%20AviF%C3%A1cil,%20necesito%20ayuda%20con%20el%20registro%20de%20mi%20granja"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg
                    className="w-8 h-8 fill-current drop-shadow-xs transition-transform duration-200 group-hover:scale-105"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
                  </svg>
                </a>
              </div>

              {/* Recorte de Elie Señalando a la Derecha Desktop con Framer Motion */}
              <motion.div
                variants={elieVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={styles.elieContainer}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/Sign-up/elie-senalando-derecha.webp"
                  alt="Elie señalando a la derecha"
                  className={styles.elieImg}
                />
              </motion.div>
            </motion.div>
          ) : pasoActual === 2 ? (
            /* ────────────────────────────────────────────────────────────────────────
               ─── PASO 2: Cuenta y Titular Legal (#70a32c Verde Orgánico) ───
               ──────────────────────────────────────────────────────────────────────── */
            <motion.div
              key="step-2"
              custom={direccionAnimacion}
              variants={stepTransitionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full relative flex flex-col md:flex-row md:h-full md:overflow-hidden h-auto min-h-full overflow-visible flex-1"
            >
              {/* Tarjeta Izquierda Blanca Desktop (65% | Bottom Sheet en Móvil) */}
              <motion.div
                variants={cardMotionVariants}
                custom={direccionAnimacion}
                className={styles.step2LeftCard}
              >
                {/* Botones Superiores Desktop (Ocultos en Móvil) */}
                <div className={styles.desktopTopActions}>
                  {/* Selector de Idioma Desktop (Arriba a la Izquierda) */}
                  <div className="absolute top-8 left-8 z-20">
                    <button
                      type="button"
                      id="lang-button-step2"
                      aria-expanded={isIdiomaModalOpen}
                      aria-haspopup="dialog"
                      onClick={() => setIsIdiomaModalOpen(true)}
                      className={styles.topActionBtn}
                    >
                      <span className="w-4.5 h-3 rounded-xs overflow-hidden inline-flex shadow-2xs border border-gray-100 flex-shrink-0">
                        {currentLang.flag}
                      </span>
                      <span className="text-[11.5px] font-semibold tracking-wide text-gray-800">
                        {currentLang.label}
                      </span>
                      <span className="material-symbols-outlined text-[16px] text-gray-400">
                        expand_more
                      </span>
                    </button>
                  </div>

                  {/* Botón Volver Desktop (Arriba a la Derecha) */}
                  <div className="absolute top-8 right-8 z-20">
                    <button
                      type="button"
                      onClick={retrocederPaso}
                      className={styles.topActionBtn}
                    >
                      <span className="material-symbols-outlined text-[16px] text-gray-500">arrow_back</span>
                      <span>Volver</span>
                    </button>
                  </div>
                </div>

                {/* Formulario Paso 2 */}
                <Step2UsuarioForm
                  initialData={paso2}
                  onSubmit={guardarPaso2}
                  onBack={retrocederPaso}
                  onResetValidation={resetValidaciones}
                />

                {/* Espacio en blanco móvil para que los botones CTA y enlaces suban holgadamente sobre la barra de navegación */}
                <div className={styles.mobileScrollSpacer} aria-hidden="true" />
              </motion.div>

              {/* ─── Columna Derecha Verde Desktop (35%): Logo, Redes, Elie ─── */}
              <div className={styles.step2RightColumn} />

              {/* Logo AviFácil Superior Derecho en Verde Desktop */}
              <div className={styles.step2LogoContainer}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center shadow-md overflow-hidden shrink-0"
                  style={{ backgroundColor: '#FFB800' }}
                >
                  <Image
                    src="/Sign-up/avifacil-logo-badge.png"
                    alt="AviFácil Icono"
                    width={36}
                    height={36}
                    priority
                    className="w-9 h-9 object-contain select-none pointer-events-none"
                  />
                </div>
                <span
                  className="text-3xl font-extrabold tracking-tight whitespace-nowrap leading-none select-none text-white drop-shadow-xs"
                  style={{ fontFamily: "'Caveat', cursive", fontSize: '32px' }}
                >
                  AviFácil
                </span>
              </div>

              {/* Iconos de Redes Sociales Verticales Paso 2 Desktop (Mismo estilo que en Paso 1 pero en color Verde #70a32c) */}
              <div className={styles.step2SocialContainer}>
                {/* Instagram */}
                <a
                  aria-label="Instagram"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#70a32c] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                {/* Facebook */}
                <a
                  aria-label="Facebook"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#70a32c] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Facebook"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                {/* YouTube */}
                <a
                  aria-label="YouTube"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#70a32c] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="YouTube"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
                {/* Google */}
                <a
                  aria-label="Google"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#70a32c] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                  href="https://google.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Google"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                    <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
                  </svg>
                </a>
                {/* TikTok */}
                <a
                  aria-label="TikTok"
                  className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#70a32c] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                  href="https://tiktok.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="TikTok"
                >
                  <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
                  </svg>
                </a>
              </div>

              {/* Recorte de Elie Señalando a la Izquierda Desktop con Framer Motion */}
              <motion.div
                variants={elieVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={styles.step2ElieContainer}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/Sign-up/elie-senalando-izquierda.webp"
                  alt="Elie señalando a la izquierda"
                  className={styles.step2ElieImg}
                />
              </motion.div>

              {/* Botones Flotantes Desktop Paso 2 (Mismo Avatar Elie y WhatsApp que en Paso 1) */}
              <div className={styles.step2FloatingSupportDesktop}>
                {showElieTooltip && (
                  <div className="absolute bottom-16 left-0 w-64 bg-white rounded-2xl p-3 shadow-xl border border-purple-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-50">
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <span className="font-bold text-[#c25ee8]">¡Hola! Soy Elie 🐥</span>
                      <button
                        type="button"
                        onClick={() => setShowElieTooltip(false)}
                        className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-600 leading-relaxed">
                      Estoy aquí para acompañarte en tu registro. Si tienes cualquier duda sobre tus datos, ¡avísame!
                    </p>
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowElieTooltip((prev) => !prev)}
                    aria-label="Abrir ayuda de Elie Asistente"
                    className="w-14 h-14 rounded-full bg-[#c25ee8] border-2 border-white shadow-xl overflow-hidden flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105 focus:outline-none"
                  >
                    <Image
                      src="/Sign-up/elie-avatar.webp"
                      alt="Avatar de Elie Asistente"
                      width={56}
                      height={56}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center top' }}
                    />
                  </button>
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-xs pointer-events-none z-10" />
                </div>

                <div className="relative flex items-center justify-center">
                  <span
                    className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#70a32c] ${styles.waRadarRing}`}
                  />
                  <span
                    className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#70a32c] ${styles.waRadarRingDelayed}`}
                  />
                  <button
                    type="button"
                    aria-label="Contactar por WhatsApp"
                    className={`relative flex items-center justify-center w-14 h-14 rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer group bg-[#70a32c] hover:bg-[#5d8b24] border-0 ${styles.step2WaGlowBtn}`}
                    onClick={handleOpenWhatsApp}
                  >
                    <svg
                      className="w-8 h-8 fill-current drop-shadow-xs transition-transform duration-200 group-hover:scale-105"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
                    </svg>
                  </button>
                </div>
              </div>
            </motion.div>
          ) : pasoActual === 3 ? (
            /* ────────────────────────────────────────────────────────────────────────
               ─── PASO 3: Acceso y Seguridad (#EE5847 Coral | Elie a la Izquierda) ───
               ──────────────────────────────────────────────────────────────────────── */
            <motion.div
              key="step-3"
              custom={direccionAnimacion}
              variants={stepTransitionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full relative flex flex-col md:flex-row md:h-full md:overflow-hidden h-auto min-h-full overflow-visible flex-1"
            >
              {/* Columna Izquierda Coral Desktop (45%): Logo, Redes, Elie a la Izquierda */}
              <div className={styles.step3LeftColumn}>
                <div className={styles.logoContainer}>
                  <Link
                    href={ROUTES.HOME}
                    className="flex items-center gap-2.5 group"
                    title="AviFácil — Inicio"
                  >
                    <div
                      className="rounded-full bg-[#FFB800] flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105 flex-shrink-0"
                      style={{ width: '36px', height: '36px' }}
                    >
                      <Image
                        src="/Sign-up/avifacil-logo-badge.png"
                        alt="Logo AviFácil"
                        width={22}
                        height={22}
                        priority
                        className="object-contain drop-shadow-xs w-5 h-5 pointer-events-none"
                      />
                    </div>
                    <span
                      className="text-white font-bold text-2xl lg:text-[26px] tracking-wide select-none drop-shadow-xs leading-none"
                      style={{ fontFamily: "'Caveat', cursive" }}
                    >
                      AviFácil
                    </span>
                  </Link>
                </div>

                {/* Iconos de Redes Sociales Verticales Desktop Coral */}
                <div className={styles.step3SocialContainer}>
                  <a
                    aria-label="Instagram"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#EE5847] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </a>
                  <a
                    aria-label="Facebook"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#EE5847] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </a>
                  <a
                    aria-label="YouTube"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#EE5847] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                    href="https://youtube.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                    </svg>
                  </a>
                  <a
                    aria-label="Google"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#EE5847] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                    href="https://google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
                    </svg>
                  </a>
                  <a
                    aria-label="TikTok"
                    className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-[#EE5847] shadow-xs hover:scale-110 transition-transform duration-200 cursor-pointer"
                    href="https://tiktok.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24">
                      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.24 1.07-.14 1.61.24 1.64 1.82 2.89 3.5 2.77 1.45-.04 2.76-.98 3.19-2.36.14-.48.2-1 .2-1.51.01-5.77.01-11.53.01-17.3z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Tarjeta Derecha Blanca (Desktop 65% | Bottom Sheet en Móvil) */}
              <motion.div
                variants={cardMotionVariants}
                custom={direccionAnimacion}
                className={styles.rightCard}
              >
                {/* Botones Superiores Desktop (Ocultos en Móvil) */}
                <div className={styles.desktopTopActions}>
                  {/* Botón Volver Desktop (Arriba a la Izquierda) */}
                  <div className="absolute top-8 left-8 z-20">
                    <button
                      type="button"
                      onClick={retrocederPaso}
                      className={styles.topActionBtn}
                    >
                      <span className="material-symbols-outlined text-[16px] text-gray-500">arrow_back</span>
                      <span>Volver</span>
                    </button>
                  </div>

                  {/* Selector de Idioma Desktop (Arriba a la Derecha) */}
                  <div className="absolute top-8 right-8 z-20">
                    <button
                      type="button"
                      id="lang-button-step3"
                      aria-expanded={isIdiomaModalOpen}
                      aria-haspopup="dialog"
                      onClick={() => setIsIdiomaModalOpen(true)}
                      className={styles.topActionBtn}
                    >
                      <span className="w-4.5 h-3 rounded-xs overflow-hidden inline-flex shadow-2xs border border-gray-100 flex-shrink-0">
                        {currentLang.flag}
                      </span>
                      <span className="text-[11.5px] font-semibold tracking-wide text-gray-800">
                        {currentLang.label}
                      </span>
                      <span className="material-symbols-outlined text-[16px] text-gray-400">
                        expand_more
                      </span>
                    </button>
                  </div>
                </div>

                {/* Formulario Paso 3 */}
                <Step3CredencialesForm
                  initialData={paso3}
                  paso2Email={paso2.email}
                  emailValidation={emailValidation}
                  isSubmitting={isSubmitting}
                  onSubmit={completarRegistro}
                  onValidarEmail={validarEmailAntiColision}
                  onBack={retrocederPaso}
                />

                {/* Espacio en blanco móvil para que los botones CTA y enlaces suban holgadamente sobre la barra de navegación */}
                <div className={styles.mobileScrollSpacer} aria-hidden="true" />
              </motion.div>

              {/* Botones Flotantes Desktop Paso 3 (WhatsApp Coral y Avatar Elie) */}
              <div className={styles.floatingElieAvatarDesktop}>
                {showElieTooltip && (
                  <div className="absolute bottom-16 right-0 w-64 bg-white rounded-2xl p-3 shadow-xl border border-rose-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-40">
                    <div className="flex items-start justify-between gap-1 mb-1">
                      <span className="font-bold text-[#EE5847]">¡Casi listos! 🐥</span>
                      <button
                        type="button"
                        onClick={() => setShowElieTooltip(false)}
                        className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                      >
                        ✕
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-600 leading-relaxed">
                      Este es el último paso. Crea tu clave secreta para entrar a tu granja cuando quieras.
                    </p>
                  </div>
                )}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowElieTooltip((prev) => !prev)}
                    aria-label="Abrir ayuda de Elie Asistente"
                    className="w-14 h-14 rounded-full bg-[#EE5847] border-2 border-white shadow-xl overflow-hidden flex items-center justify-center cursor-pointer transition-transform duration-200 hover:scale-105 focus:outline-none"
                  >
                    <Image
                      src="/Sign-up/elie-avatar.webp"
                      alt="Avatar de Elie Asistente"
                      width={56}
                      height={56}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: 'center top' }}
                    />
                  </button>
                  <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 border-2 border-white rounded-full shadow-xs pointer-events-none z-10" />
                </div>
              </div>

              <div className={styles.floatingWhatsappDesktop}>
                <span
                  className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#EE5847] ${styles.waRadarRing}`}
                />
                <span
                  className={`absolute w-14 h-14 rounded-full pointer-events-none bg-[#EE5847] ${styles.waRadarRingDelayed}`}
                />
                <button
                  type="button"
                  aria-label="Contactar por WhatsApp"
                  className={`relative flex items-center justify-center w-14 h-14 rounded-full text-white transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer group bg-[#EE5847] hover:bg-[#D94534] border-0 ${styles.step3WaGlowBtn}`}
                  onClick={handleOpenWhatsApp}
                >
                  <svg
                    className="w-8 h-8 fill-current drop-shadow-xs transition-transform duration-200 group-hover:scale-105"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
                  </svg>
                </button>
              </div>

              {/* Recorte de Elie Señalando a la Derecha Desktop en Paso 3 */}
              <motion.div
                variants={elieVariants}
                initial="enter"
                animate="center"
                exit="exit"
                className={styles.step3ElieContainer}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/Sign-up/elie-senalando-derecha.webp"
                  alt="Elie señalando a la derecha"
                  className={styles.step3ElieImg}
                />
              </motion.div>
            </motion.div>
          ) : (
            /* ────────────────────────────────────────────────────────────────────────
               ─── PASO 4: Bienvenida y Regalo Especial (#70a32c Verde) ───
               ──────────────────────────────────────────────────────────────────────── */
            <motion.div
              key="step-4"
              custom={direccionAnimacion}
              variants={stepTransitionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="w-full h-full flex flex-1 overflow-y-auto md:overflow-hidden"
            >
              <Step4BienvenidaView
                nombreUsuario={paso2.primer_nombre}
                nombreGranja={paso1.nombre_granja}
                onComenzar={finalizarOnboarding}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* ─── Widget de Soporte Flotante Móvil (Solo visible en Pasos 1, 2 y 3) ─── */}
      {!isStep4 && (
        <aside className={styles.floatingSupportMobile} data-purpose="floating-support">
          {/* Avatar Elie con tooltip */}
          <div className="relative cursor-pointer group" title="Habla con Elie Asistente">
            {showElieTooltip && (
              <div className="absolute bottom-12 right-0 w-60 bg-white rounded-2xl p-3 shadow-xl border border-purple-100 text-xs text-gray-700 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200 z-40">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <span className="font-bold text-[#c25ee8]">¡Hola! Soy Elie 🐥</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowElieTooltip(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xs cursor-pointer"
                  >
                    ✕
                  </button>
                </div>
                <p className="text-[11px] text-gray-600 leading-relaxed">
                  Estoy aquí para acompañarte en tu registro. Si tienes cualquier duda sobre tus datos, ¡avísame!
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowElieTooltip((prev) => !prev)}
              className="w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden transition group-hover:scale-105 flex items-center justify-center cursor-pointer p-0 bg-[#c25ee8]"
              aria-label="Ayuda de Elie"
            >
              <Image
                src="/Sign-up/elie-avatar.webp"
                alt="Elie"
                width={40}
                height={40}
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center top' }}
              />
            </button>
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full pointer-events-none" />
          </div>

          {/* Botón WhatsApp Móvil */}
          <button
            type="button"
            aria-label="Contactar por WhatsApp"
            className={`w-11 h-11 rounded-full text-white flex items-center justify-center shadow-lg transition-transform cursor-pointer active:scale-90 border-0 ${
              pasoActual === 3
                ? 'bg-[#EE5847] hover:bg-[#D94534]'
                : pasoActual === 2
                ? 'bg-[#70a32c] hover:bg-[#5d8b24]'
                : 'bg-[#c25ee8] hover:bg-[#b145dc]'
            }`}
            onClick={handleOpenWhatsApp}
          >
            <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
              <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.312.045-.694.06-2.006-.484-1.579-.655-2.613-2.257-2.695-2.366-.08-.109-.646-.86-.646-1.639 0-.779.408-1.163.553-1.32.145-.157.319-.196.425-.196.106 0 .213.001.305.006.1.005.234-.038.366.279.136.327.464 1.132.505 1.215.041.083.068.18.014.288-.053.109-.08.177-.16.27-.08.093-.169.208-.241.28-.08.08-.163.167-.07.327.093.16.413.682.887 1.103.61.543 1.124.71 1.284.79.16.08.254.068.349-.041.095-.109.407-.474.516-.638.109-.164.218-.136.368-.082.15.054.954.45 1.118.532.164.082.273.123.313.191.041.069.041.402-.103.807zM12 2C6.477 2 2 6.477 2 12c0 1.891.527 3.66 1.443 5.176L2 22l4.981-1.306A9.957 9.957 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18.167c-1.697 0-3.272-.51-4.585-1.385l-.329-.22-2.96.776.79-2.885-.24-.383A8.134 8.134 0 013.833 12c0-4.503 3.664-8.167 8.167-8.167 4.503 0 8.167 3.664 8.167 8.167 0 4.503-3.664 8.167-8.167 8.167z" />
            </svg>
          </button>
        </aside>
      )}

      {/* ─── Modal Limpio de Selección de Idioma ─── */}
      <IdiomaModalSelector
        isOpen={isIdiomaModalOpen}
        currentCode={(paso1.idioma || 'es') as 'es' | 'en' | 'pt'}
        idiomas={IDIOMAS}
        onSelect={handleSelectLang}
        onClose={() => setIsIdiomaModalOpen(false)}
      />

      {/* ─── Modal Central de Manejo de Errores ─── */}
      <OnboardingErrorModal
        isOpen={errorModal.isOpen}
        tipo={errorModal.tipo}
        titulo={errorModal.titulo}
        mensaje={errorModal.mensaje}
        detalleContextual={errorModal.detalleContextual}
        onAccionPrincipal={errorModal.onAccionPrincipal}
        onAccionSecundaria={errorModal.onAccionSecundaria}
        onClose={cerrarErrorModal}
      />

      {/* ─── Modal Individual de Red Social / Canal Oficial (Diseño Propio) ─── */}
      <SocialIndividualModal
        isOpen={isSocialModalOpen}
        onClose={() => setIsSocialModalOpen(false)}
        network={activeSocialNetwork}
      />
    </motion.div>
  );
}
